import { setOverlayVisible } from "../../redux/actions/overlay";
import { addTask } from "../../redux/actions/tasks";
import { useState } from "react";
import useTaskAPIClient from "../../api/task/hooks/useTaskAPIClient";
import useTaskUsage from "./useTaskUsage";
import { useDispatch, useSelector } from "react-redux";
import { isNetworkError } from "../../helpers/networkErrors";

const useCreateTask = () => {
  const dispatch = useDispatch();
  const { subscription } = useSelector((state) => state.user);
  const [saving, setSaving] = useState(false);
  const { createTask } = useTaskAPIClient();
  const { tasksUsed, taskLimit } = useTaskUsage();

  const onCreateTask = async (newTask, noteInputValueRef) => {
    if (subscription?.plan === "free" && tasksUsed >= taskLimit) {
      dispatch(
        setOverlayVisible({
          visible: true,
          overlayType: "subscribe",
        }),
      );

      return false;
    }
    setSaving(true);
    const newNotes = { ...newTask.notes };
    const noteInputValue = noteInputValueRef?.current?.getNoteValue();

    if (noteInputValue) {
      const newNoteId = +new Date();
      newNotes[newNoteId] = {
        text: noteInputValue,
        id: newNoteId,
        completed: false,
      };
    }

    try {
      const taskToCreate = {
        ...newTask,
        notes: newNotes,
      };

      // Set targetedAt when creating a targeted task
      if (newTask.targeted) {
        taskToCreate.targetedAt = new Date().toISOString();
      }

      const { taskId, created } = await createTask({
        task: taskToCreate,
      });

      dispatch(addTask({ task: { ...taskToCreate, created }, taskId }));
      setSaving(false);
      return true;
    } catch (error) {
      setSaving(false);
      // Handle 402 Payment Required - user hit task limit or subscription expired
      if (error?.status === 402 || error?.response?.status === 402) {
        dispatch(
          setOverlayVisible({
            visible: true,
            overlayType: "subscribe",
          }),
        );
        return false;
      }
      // For network/offline errors, return false - the global handler or useApiClient
      // will show the offline dialog
      if (isNetworkError(error)) {
        return false;
      }
      // Re-throw other errors
      throw error;
    }
  };

  return { createTask: onCreateTask, saving };
};

export default useCreateTask;
